import React from 'react';
import PropTypes from 'prop-types';
import { FaBook, FaCalendarAlt, FaGlobeEurope, FaMusic } from 'react-icons/fa';
import { localeFor, useTranslation } from '../../utils/translations';
import Avatar from '../Common/Avatar';
import TeacherContacts from './TeacherContacts';
import { dayKey, peopleContacts } from './familyCompat';
import './StudentGroupedView.css';

const iconForActivity = (id) => {
  switch (id) {
    case 'sundaySchool':
      return <FaBook aria-hidden="true" />;
    case 'russianSchool':
      return <FaGlobeEurope aria-hidden="true" />;
    case 'childrensChoir':
      return <FaMusic aria-hidden="true" />;
    default:
      return null;
  }
};

// Format birthdate for display, in the app language's locale
const formatBirthdate = (birthdate, language) => {
  if (!birthdate) return '';
  try {
    const [year, month, day] = birthdate.split('-').map(Number);
    const date = new Date(year, month - 1, day); // local date, not UTC
    return date.toLocaleDateString(localeFor(language), {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (error) {
    return birthdate; // Return original if parsing fails
  }
};

// Whole years since the birthdate, or null when it cannot be parsed
const ageOf = (birthdate) => {
  try {
    const [y, m, d] = birthdate.split('-').map(Number);
    const now = new Date();
    let age = now.getFullYear() - y;
    if (now.getMonth() + 1 < m || (now.getMonth() + 1 === m && now.getDate() < d)) age -= 1;
    return Number.isFinite(age) ? age : null;
  } catch (error) {
    return null;
  }
};

const StudentGroupedView = ({ students, activities }) => {
  const { t, tn, language } = useTranslation();
  
  // Helper to get a student's activity details by id
  const getStudentActivity = (student, activityId) =>
    student.activities.find((sa) => sa.id === activityId);

  return (
    <div className="student-grouped-layout" role="main" aria-label={t('details.studentsGroupedView')}>
      {students.map((student) => {
        // Count how many activities this student is enrolled in
        const enrolledActivities = ['sundaySchool', 'russianSchool', 'childrensChoir'].filter((aid) => {
          const sa = getStudentActivity(student, aid);
          return sa?.details;
        });

        return (
          <div key={student.id} className="student-container">
            <div className="student-container-header">
              <h3 className="student-title">
                <Avatar name={student.name} gender={student.gender} />
                {student.name}
              </h3>
              <span className="student-count">
                {tn('details.activities', enrolledActivities.length)}
              </span>
            </div>
            {student.birthdate && (
              <div className="student-birthday-info">
                <span className="birthday-label">{t('details.birthday')}:</span>
                <span className="birthday-value">
                  {formatBirthdate(student.birthdate, language)}
                  {ageOf(student.birthdate) !== null && ` · ${tn('details.age', ageOf(student.birthdate))}`}
                </span>
              </div>
            )}
            <div className="activities-in-student">
              {['sundaySchool', 'russianSchool', 'childrensChoir'].map((aid) => {
                const activity = activities.find(a => a.id === aid);
                const sa = getStudentActivity(student, aid);
                
                if (!sa?.details) {
                  return null; // Don't show activities the student isn't enrolled in
                }
                
                const d = sa.details;
                const room = d.classroom || d.choirRoom;
                const main = d.class || d.choirNumber;
                // Names plus phone numbers where the payload carries them
                const people = peopleContacts(d);
                const labels = aid === 'childrensChoir'
                  ? { main: t('details.choirNumber'), people: t('details.choirLeaders') }
                  : { main: t('details.class'), people: t('details.teachers') };

                return (
                  <div key={aid} className="activity-in-student-card" data-activity={aid}>
                    <div className="activity-header-info">
                      <div className="activity-name-with-icon">
                        <span className="activity-icon">{iconForActivity(aid)}</span>
                        <h4 className="activity-name">{t(`activities.${aid}`) || activity?.name || aid}</h4>
                      </div>
                      {d.day && (
                        <span className="day-badge"><FaCalendarAlt aria-hidden="true" /> {dayKey(d.day) ? t(dayKey(d.day)) : d.day}</span>
                      )}
                    </div>
                    <div className="activity-details">
                      <div className="detail-row">
                        <span className="detail-label">{labels.main}:</span>
                        <span className="detail-value">{main}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">{t('details.room')}:</span>
                        <span className="detail-value">{room}</span>
                      </div>
                      {people.length > 0 && (
                        <div className="detail-row">
                          <span className="detail-label">{labels.people}:</span>
                          <span className="detail-value">
                            <TeacherContacts people={people} />
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

StudentGroupedView.propTypes = {
  students: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      birthdate: PropTypes.string,
      activities: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          status: PropTypes.string,
          details: PropTypes.object,
        })
      ).isRequired,
    })
  ).isRequired,
  activities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      day: PropTypes.string,
    })
  ).isRequired,
};

export default StudentGroupedView;

